/**
 * S-139 (07.10.2026, właściciel): „jak zatwierdzam skład, to dobrze, jakby nie odświeżała się cała strona, tylko płynnie
 * ta oferta znikała; zrób audyt takich odświeżeń i ogarnij wszystkie takie miejsca”.
 *
 * Wspólne klocki płynnych zapisów (reguła z 09.07: zapis = żądanie w tle → toast → PUNKTOWA zmiana DOM; scroll,
 * rozwinięcia, fokus, filtry i zaznaczenie przeżywają zapis; pełne przerysowanie tylko przy zmianie struktury — i wtedy
 * bez spinnera „Wczytuję…”, bez skoku na górę):
 *  - `wygasIUsun`     — pozycja znika z listy: wygaszenie + zwinięcie wysokości, potem `remove()` (i ewentualny callback),
 *  - `zapamietajWidok`— zdjęcie scrolla (okno, główny kontener, przewijane przodki, sam host), otwartych `<details>` i fokusu;
 *                       zwraca funkcję, która to odtwarza po przerysowaniu,
 *  - `odswiezCicho`   — „najpierw pobierz, potem podmień”: spinner tylko przy pustym hoście, potem podmiana z odtworzeniem widoku,
 *  - `maNiezapisaneZmiany` — strażnik: nie nadpisujemy formularza, w którym ktoś coś wpisał,
 *  - `podmienElement` — jeden wiersz/karta z odpowiedzi zapisu zamiast całej listy,
 *  - `koalescuj`      — seria zdarzeń (np. 5× `sb:product:changed`) → jedno odświeżenie z zebranymi kluczami.
 */

declare const lucide: { createIcons?: (opts?: { nodes?: Element[] }) => void } | undefined;

const ruchOgraniczony = (): boolean => {
  try { return typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches; }
  catch { return false; }
};

/** Ikony lucide tylko w podanym fragmencie (bez przeszukiwania całej strony). */
export function ikony(root: Element | null | undefined): void {
  if (!root) return;
  try { if (typeof lucide !== 'undefined' && lucide && typeof lucide.createIcons === 'function') lucide.createIcons({ nodes: [root] }); }
  catch { /* ikony są dekoracją — brak lucide nie psuje zapisu */ }
}

export type OpcjeWygaszenia = { ms?: number; zwin?: boolean; poUsunieciu?: () => void };

/**
 * Pozycja znika z listy płynnie: wygaszenie, zwinięcie wysokości (żeby niższe wiersze nie „skakały”), potem `remove()`.
 * Wiersze tabel (`TR`) tylko wygaszamy — wysokości wiersza tabeli nie da się płynnie zwinąć. Bez animacji, gdy system
 * prosi o ograniczenie ruchu. Drugie wywołanie na tym samym elemencie nic nie robi. Zwraca `true`, gdy element był w DOM.
 */
export function wygasIUsun(el: Element | null | undefined, opts: OpcjeWygaszenia = {}): boolean {
  const e = el as HTMLElement | null | undefined;
  if (!e || !e.isConnected) { opts.poUsunieciu?.(); return false; }
  // drugie wywołanie w trakcie wygaszania: bez drugiej animacji, ale jego `poUsunieciu` też się wykona
  const czekajace: Array<() => void> = ((e as any).__sbPoUsunieciu ||= []);
  if (opts.poUsunieciu) czekajace.push(opts.poUsunieciu);
  if (e.dataset.sbWygasanie === '1') return true;
  e.dataset.sbWygasanie = '1';
  const ms = ruchOgraniczony() ? 0 : Math.max(0, opts.ms ?? 220);
  const koniec = (): void => { e.remove(); czekajace.splice(0).forEach(f => { try { f(); } catch { /* callback wołającego */ } }); };
  if (!ms) { koniec(); return true; }
  const zwin = opts.zwin !== false && e.tagName !== 'TR';
  e.style.pointerEvents = 'none';
  if (zwin) {
    const h = e.getBoundingClientRect().height;
    e.style.boxSizing = 'border-box';
    e.style.overflow = 'hidden';
    e.style.maxHeight = h + 'px';
    const pol = Math.round(ms / 2);
    e.style.transition = `opacity ${ms}ms ease, max-height ${ms}ms ease ${pol}ms, margin ${ms}ms ease ${pol}ms, padding ${ms}ms ease ${pol}ms, border-width ${ms}ms ease ${pol}ms`;
    requestAnimationFrame(() => {
      e.style.opacity = '0';
      e.style.maxHeight = '0px';
      e.style.marginTop = '0px'; e.style.marginBottom = '0px';
      e.style.paddingTop = '0px'; e.style.paddingBottom = '0px';
      e.style.borderTopWidth = '0px'; e.style.borderBottomWidth = '0px';
    });
    window.setTimeout(koniec, ms + pol + 30);
  } else {
    e.style.transition = `opacity ${ms}ms ease`;
    requestAnimationFrame(() => { e.style.opacity = '0'; });
    window.setTimeout(koniec, ms + 30);
  }
  return true;
}

const przewijalny = (el: Element): boolean => {
  const h = el as HTMLElement;
  if (!h || !(h.scrollHeight > h.clientHeight + 1)) return false;
  try { const o = getComputedStyle(h).overflowY; return o === 'auto' || o === 'scroll' || o === 'overlay'; } catch { return false; }
};

const kluczDetails = (d: HTMLDetailsElement, i: number): string =>
  d.id || d.dataset.key || d.dataset.id || d.getAttribute('data-klucz') || (d.querySelector('summary')?.textContent || '').trim().slice(0, 80) || String(i);

// 19.10.2026 (właściciel: monter w grupie ZMONTOWAĆ „wywalało całkiem gdzie indziej”): selektor brał PIERWSZY atrybut
// data-, a wiersze panelu grup mają go wspólny (data-filter-type="manual-group") — po przerysowaniu panelu fokus
// lądował na pierwszej grupie roboczej, a Enter ze skanera / Spacja „klikały” ją (wiersz ma role=button).
// Teraz selektor składa się ze WSZYSTKICH krótkich atrybutów name / value pola wyboru / data- i musi wskazywać
// w hoście DOKŁADNIE jeden element — inaczej fokusu nie odtwarzamy wcale.
export function selektorFokusu(el: Element, host: Element): string {
  if (!(el instanceof HTMLElement) || !host.contains(el) || el === host) return '';
  if (el.id) return '#' + CSS.escape(el.id);
  const wyborowe = el.tagName === 'INPUT' && /^(radio|checkbox)$/i.test(el.getAttribute('type') || '');
  const czesci = [...el.attributes]
    .filter(a => (a.name === 'name' || a.name.startsWith('data-') || (wyborowe && a.name === 'value')) && a.value && a.value.length < 80)
    .map(a => `[${a.name}="${CSS.escape(a.value)}"]`);
  if (!czesci.length) return '';
  const sel = el.tagName.toLowerCase() + czesci.join('');
  try {
    return host.querySelectorAll(sel).length === 1 ? sel : '';
  } catch {
    return '';
  }
}

/**
 * Zdjęcie stanu widoku przed przerysowaniem `host`: scroll okna, głównego kontenera, przewijanych przodków i samego
 * hosta; otwarte `<details>` w hoście (po id / data-key / treści summary); fokus i zaznaczenie tekstu w polu hosta.
 * Zwraca funkcję, która to odtwarza (wołać PO podmianie treści).
 */
export function zapamietajWidok(host?: Element | null): () => void {
  const przewijane: Array<[HTMLElement, number]> = [];
  const dodaj = (el: Element | null | undefined): void => {
    if (el && el instanceof HTMLElement && !przewijane.some(([x]) => x === el)) przewijane.push([el, el.scrollTop]);
  };
  const oknoY = typeof window !== 'undefined' ? window.scrollY : 0;
  dodaj(document.scrollingElement);
  dodaj(document.querySelector('.main-content'));
  if (host) {
    if (przewijalny(host)) dodaj(host);
    for (let p = host.parentElement; p; p = p.parentElement) if (przewijalny(p)) dodaj(p);
  }
  // przewinięte potomki z id (lista w szufladzie, tabela w karcie) — odtwarzane po id także po podmianie treści
  const potomkowie: Array<[string, number]> = [];
  host?.querySelectorAll<HTMLElement>('[id]').forEach(el => { if (el.scrollTop > 0) potomkowie.push([el.id, el.scrollTop]); });
  const hostId = host instanceof HTMLElement ? host.id : '';
  const hostTop = host instanceof HTMLElement ? host.scrollTop : 0;
  const otwarte = new Map<string, boolean>();
  host?.querySelectorAll<HTMLDetailsElement>('details').forEach((d, i) => otwarte.set(kluczDetails(d, i), d.open));
  const aktywny = document.activeElement;
  const fokus = host && aktywny ? selektorFokusu(aktywny, host) : '';
  const pole = aktywny as HTMLInputElement | null;
  const zaznaczenie = fokus && pole && typeof pole.selectionStart === 'number' ? [pole.selectionStart, pole.selectionEnd ?? pole.selectionStart] : null;
  return () => {
    // host podmieniony w całości (np. szuflada zbudowana od nowa) — szukamy następcy po id
    if (host && !host.isConnected && hostId) {
      const nastepca = document.getElementById(hostId);
      if (nastepca) { host = nastepca; if (hostTop && Math.abs(nastepca.scrollTop - hostTop) > 1) nastepca.scrollTop = hostTop; }
    }
    if (host && otwarte.size) host.querySelectorAll<HTMLDetailsElement>('details').forEach((d, i) => {
      const k = kluczDetails(d, i);
      if (otwarte.has(k)) d.open = !!otwarte.get(k);
    });
    if (host && fokus) {
      const el = host.querySelector<HTMLElement>(fokus);
      if (el && document.activeElement !== el) {
        try { el.focus({ preventScroll: true }); } catch { /* element nieaktywny */ }
        if (zaznaczenie) { try { (el as HTMLInputElement).setSelectionRange(zaznaczenie[0], zaznaczenie[1]); } catch { /* pole bez zaznaczenia */ } }
      }
    }
    // najpierw w (aktualnym) hoście — te same id bywają w szufladzie głównej i zagnieżdżonej
    potomkowie.forEach(([id, top]) => {
      const el = (host && host.querySelector<HTMLElement>('#' + CSS.escape(id))) || document.getElementById(id);
      if (el && Math.abs(el.scrollTop - top) > 1) el.scrollTop = top;
    });
    przewijane.forEach(([el, top]) => { if (el.isConnected && Math.abs(el.scrollTop - top) > 1) el.scrollTop = top; });
    if (typeof window !== 'undefined' && Math.abs(window.scrollY - oknoY) > 1) window.scrollTo({ top: oknoY });
  };
}

/** Czy w hoście jest pole zmienione przez użytkownika i jeszcze niezapisane (wartość ≠ wartość z renderu). */
export function maNiezapisaneZmiany(host: Element | null | undefined): boolean {
  if (!host) return false;
  for (const el of host.querySelectorAll<HTMLElement>('input, textarea, select')) {
    if (el.closest('[data-sb-bez-strazy]')) continue;
    if (el instanceof HTMLInputElement) {
      if (['search', 'hidden', 'button', 'submit', 'reset', 'file'].includes(el.type)) continue;
      if (el.type === 'checkbox' || el.type === 'radio') { if (el.checked !== el.defaultChecked) return true; continue; }
      if (el.value !== el.defaultValue) return true;
    } else if (el instanceof HTMLTextAreaElement) {
      if (el.value !== el.defaultValue) return true;
    } else if (el instanceof HTMLSelectElement) {
      if ([...el.options].some(o => o.selected !== o.defaultSelected)
          && !(el.options.length && ![...el.options].some(o => o.defaultSelected) && el.selectedIndex === 0)) return true;
    }
  }
  return false;
}

export type OpcjeOdswiezenia<T> = {
  pobierz: () => Promise<T>;
  /** zwraca HTML hosta (podmieniany w całości) albo sam rysuje i zwraca void */
  rysuj: (dane: T, host: HTMLElement) => string | void;
  /** placeholder tylko dla PUSTEGO hosta (pierwsze wejście) */
  spinner?: string;
  /** po podmianie: podpięcie zdarzeń itp. (ikony lucide dorysowujemy sami) */
  poRysowaniu?: (host: HTMLElement, dane: T) => void;
  /** nie nadpisuj, gdy w hoście są niezapisane zmiany formularza (zwraca dane bez rysowania) */
  strzezFormularza?: boolean;
};

/**
 * „Najpierw pobierz, potem podmień”: host z treścią zostaje widoczny aż do przyjścia danych (bez spinnera i bez
 * czyszczenia), potem jedna podmiana z odtworzeniem scrolla, rozwinięć i fokusu. Spinner tylko dla pustego hosta.
 * Błąd pobrania zostawia starą treść i leci wyżej (wołający pokazuje toast).
 */
export async function odswiezCicho<T>(host: HTMLElement | null | undefined, o: OpcjeOdswiezenia<T>): Promise<T | null> {
  if (!host) return null;
  const pusty = !host.childElementCount && !(host.textContent || '').trim();
  if (pusty && o.spinner) host.innerHTML = o.spinner;
  host.setAttribute('aria-busy', 'true');
  let dane: T;
  try { dane = await o.pobierz(); }
  finally { if (host.isConnected) host.removeAttribute('aria-busy'); }
  if (!host.isConnected) return dane;
  if (o.strzezFormularza && !pusty && maNiezapisaneZmiany(host)) return dane;
  const przywroc = zapamietajWidok(host);
  const html = o.rysuj(dane, host);
  if (typeof html === 'string') host.innerHTML = html;
  ikony(host);
  o.poRysowaniu?.(host, dane);
  przywroc();
  return dane;
}

/** Podmiana jednego elementu (wiersz, karta) nowym HTML-em z odpowiedzi zapisu. Zwraca nowy element (albo null). */
export function podmienElement(el: Element | null | undefined, html: string): HTMLElement | null {
  if (!el || !el.isConnected) return null;
  const tpl = document.createElement('template');
  tpl.innerHTML = String(html || '').trim();
  const nowy = tpl.content.firstElementChild as HTMLElement | null;
  if (!nowy) return null;
  el.replaceWith(nowy);
  ikony(nowy);
  return nowy;
}

/**
 * Zbija serię wywołań w jedno: `const plan = koalescuj(ids => odswiez(ids), 200); plan(id1); plan(id2)` → jedno
 * `odswiez(Set{id1,id2})` po 200 ms ciszy. `plan()` bez klucza = „wszystko” (Set z `null`).
 */
export function koalescuj<K>(fn: (klucze: Set<K | null>) => void, ms = 200): (klucz?: K | null) => void {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let zebrane = new Set<K | null>();
  return (klucz?: K | null) => {
    zebrane.add(klucz === undefined ? null : klucz);
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => { timer = null; const k = zebrane; zebrane = new Set(); fn(k); }, ms);
  };
}

// ── morfowanie: nowy HTML wchodzi w istniejące drzewo bez przebudowy tego, co się nie zmieniło ─────────────────

const ZRODLO = '__sbZrodlo';
const ikonaLucide = (s: Element, n: Element): boolean =>
  s.tagName.toLowerCase() === 'svg' && n.tagName === 'I' && !!n.getAttribute('data-lucide')
  && s.getAttribute('data-lucide') === n.getAttribute('data-lucide');

function morfujWezel(s: Node, n: Node, klucz?: string): void {
  if (s.nodeType !== n.nodeType) { s.parentNode?.replaceChild(n, s); return; }
  if (s.nodeType !== 1) { if (s.nodeValue !== n.nodeValue) s.nodeValue = n.nodeValue; return; }
  const se = s as Element, ne = n as Element;
  if (ikonaLucide(se, ne)) return;
  if (se.tagName !== ne.tagName) { se.replaceWith(ne); return; }
  for (const a of [...se.attributes]) if (!ne.hasAttribute(a.name)) se.removeAttribute(a.name);
  for (const a of [...ne.attributes]) if (se.getAttribute(a.name) !== a.value) se.setAttribute(a.name, a.value);
  morfujDzieci(se, ne, klucz);
}

function morfujDzieci(stary: Node & ParentNode, nowy: Node & ParentNode, klucz?: string): void {
  const nowe = [...nowy.childNodes];
  const kluczowy = (x: Node): string | null => (klucz && x.nodeType === 1 ? (x as Element).getAttribute(klucz) : null);
  if (klucz && nowe.some(x => kluczowy(x) !== null)) {
    // Lista z kluczem (np. karty zamówień po data-order-id): niezmieniona pozycja zostaje TYM SAMYM węzłem (zdarzenia,
    // otwarte menu, zaznaczenie, fokus); zmieniona albo nowa wchodzi w całości (świeża, do podpięcia zdarzeń).
    const mapa = new Map<string, Node>();
    stary.childNodes.forEach(x => { const k = kluczowy(x); if (k !== null) mapa.set(k, x); });
    const wynik = nowe.map(x => {
      const k = kluczowy(x);
      if (k === null) return x;
      const html = (x as Element).outerHTML;
      const s = mapa.get(k);
      // węzeł w trakcie wygaszania (wygasIUsun) nie wraca — jego timer i tak go usunie; wstawiamy świeży
      if (s && (s as any)[ZRODLO] === html && (s as HTMLElement).dataset?.sbWygasanie !== '1') { mapa.delete(k); return s; }
      (x as any)[ZRODLO] = html;
      return x;
    });
    let kursor: Node | null = stary.firstChild;
    for (const w of wynik) {
      if (w === kursor) { kursor = kursor.nextSibling; continue; }
      stary.insertBefore(w, kursor);
    }
    while (kursor) { const nast: Node | null = kursor.nextSibling; stary.removeChild(kursor); kursor = nast; }
    return;
  }
  const stare = [...stary.childNodes];
  if (stare.length !== nowe.length) { stary.replaceChildren(...nowe); return; }
  stare.forEach((s, i) => morfujWezel(s, nowe[i], klucz));
}

/**
 * Wprowadza nowy HTML do `host` BEZ przebudowy tego, co się nie zmieniło: teksty i atrybuty aktualizowane w miejscu,
 * ikony lucide (już zamienione na `<svg>`) uznawane za równe swoim `<i data-lucide>`. Z `klucz` (np. 'data-order-id')
 * pozycje listy porównywane są po kluczu i źródłowym HTML — niezmienione zostają tymi samymi węzłami. Zwraca listę
 * NOWYCH węzłów z kluczem (do podpięcia zdarzeń). Ikony dorysowywane tylko w hoście.
 */
export function morfuj(host: Element | null | undefined, html: string, opts: { klucz?: string } = {}): Element[] {
  if (!host) return [];
  const tpl = document.createElement('template');
  tpl.innerHTML = html;
  const noweZKluczem = opts.klucz ? [...tpl.content.querySelectorAll(`[${opts.klucz}]`)] : [];
  morfujDzieci(host, tpl.content, opts.klucz);
  ikony(host);
  return noweZKluczem.filter(el => el.isConnected);
}

/** Element nie jest widoczny (ukryta sekcja/zakładka) — odświeżenie można odłożyć do powrotu. */
export function niewidoczny(el: Element | null | undefined): boolean {
  const h = el as HTMLElement | null | undefined;
  return !h || !h.isConnected || h.offsetParent === null && getComputedStyle(h).position !== 'fixed';
}

/** Zmienia liczbę w tekście elementu (np. chip „K · … 11”, stopka „1–100 z 221”) o `delta` — bez przerysowania; nie schodzi poniżej 0. */
export function zmienLiczbe(el: Element | null | undefined, delta: number): void {
  if (!el || !delta) return;
  const cel = (el.querySelector('b, strong, .count, [data-liczba]') || el) as HTMLElement;
  const wzor = /(\d(?:[\d\s\u00a0]*\d)?)(?!.*\d)/;  // ostatnia liczba (także „1 234”), bez zjadania spacji po niej
  const m = (cel.textContent || '').match(wzor);
  if (!m) return;
  const n = Number(m[1].replace(/[\s\u00a0]/g, ''));
  if (!Number.isFinite(n)) return;
  cel.textContent = (cel.textContent || '').replace(wzor, String(Math.max(0, n + delta)));
}

/** Zmniejsza liczbę w tekście elementu o `o` (skrót do `zmienLiczbe(el, -o)`). */
export function zmniejszLiczbe(el: Element | null | undefined, o = 1): void { zmienLiczbe(el, -o); }
